import { isNil } from 'lodash';
import React, {
  CSSProperties,
  Dispatch,
  Key,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { RCMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { Switch } from '@shared/components/form';
import { MBIconV2 } from '@shared/components/image';
import { ArchiveRow } from '@shared/components/list';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { CombinedTagsComponent, componentTemplateOptions } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { OptionsData } from '@shared/scenes/workflows/template/options/OptionsData';
import { isAuditComponent, isInputComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { DEVELOPER_TOOLS, useFeatureFlag } from '@shared/util/featureFlags/launchDarkly';
import { i18n } from '@shared/util/i18n';
import { InputComponent } from '../../../InputComponent';
import { useSetComponentField } from '../../hooks';
import { Divider } from './divider';

type Component = InputComponent | CombinedTagsComponent;

interface Props {
  component: Component;
  firstSectionContent?: ReactNode;
  extraOptions?: ReactElement;
  extraSections?: (params: {
    closeSubmenus: VoidFunction;
    setOpenKeys: Dispatch<SetStateAction<string[]>>;
  }) => ReactNode;
  injectSectionsIn?: 'top' | 'bottom';
  includeLabel?: boolean;
  includePlaceholder?: boolean;
  menuStyle?: CSSProperties;
  onMenusVisibleChange?(openKeys: string[], previousOpenKeys: string[]): void;
}

export function MBWorkflowDefaultComponentOptions({
  component,
  firstSectionContent,
  extraOptions,
  extraSections,
  injectSectionsIn = 'bottom',
  includeLabel = true,
  includePlaceholder = true,
  menuStyle,
  onMenusVisibleChange,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [label] = useSetComponentField<string, Component>(component, 'label');
  const [placeholder, setPlaceholder] = useSetComponentField<string, InputComponent>(
    component as InputComponent,
    'placeholder',
  );
  const isDeveloperToolsEnabled = useFeatureFlag(DEVELOPER_TOOLS);

  const dispatch = useWorkflowDispatch();

  const setLabelAndPlaceholder = useCallback(
    (value: string) => {
      const args = {
        ...component,
      };
      if (includeLabel) {
        args.label = value;
      }
      if (includePlaceholder) {
        (args as InputComponent).placeholder = value;
      }
      dispatch({
        type: 'updateComponent',
        args,
      });
    },
    [dispatch, includeLabel, includePlaceholder, component],
  );

  const onArchive = useCallback(() => {
    dispatch({
      type: 'archiveComponent',
      args: component.id,
    });
  }, [dispatch, component.id]);

  const handleMenusVisibleChange = useCallback(
    (nextOpenKeys: Key[]) => {
      onMenusVisibleChange?.(nextOpenKeys as string[], openKeys);
      setOpenKeys(nextOpenKeys as string[]);
    },
    [onMenusVisibleChange, openKeys],
  );

  const optionsData = useMemo<OptionsData>(
    () => componentTemplateOptions.optionsDataForComponent(component),
    [component],
  );

  const closeSubmenus = useCallback(() => {
    setOpenKeys([]);
  }, []);

  const injectedSections = useMemo(
    () => extraSections?.({ closeSubmenus, setOpenKeys }),
    [closeSubmenus, extraSections, setOpenKeys],
  );

  const renderLabelInput = includeLabel && componentTemplateOptions.isLabelEditable(component);
  const renderPlaceholderInput =
    includePlaceholder && componentTemplateOptions.isPlaceholderEditable(component);
  const renderDivider = renderLabelInput || renderPlaceholderInput;
  const typeLabel = isAuditComponent(component)
    ? i18n('workflowTemplate.questionAnswerField')
    : i18n('workflowTemplate.fieldType');
  const labelForLabel = isAuditComponent(component)
    ? i18n('workflowTemplate.question')
    : i18n('workflowTemplate.label');

  return (
    <View>
      <RCMenu
        mode="vertical"
        openKeys={openKeys}
        onOpenChange={handleMenusVisibleChange}
        triggerSubMenuAction="click"
        style={{
          backgroundColor: 'white',
          borderRadius: grid(1),
          boxShadow: '0 0 10px #d9d9d9',
          maxHeight: '100vh',
          overflow: 'auto',
          ...(menuStyle ?? {}),
        }}
        expandIcon={null}
      >
        <View paddingHorizontal={3}>
          <MBTextOverlineBold color={colors.foregroundInactive}>{typeLabel}</MBTextOverlineBold>
        </View>
        <View paddingHorizontal={3} paddingVertical={2} row centerVertically>
          <MBIconV2 color={colors.foregroundActive} name={optionsData.icon} size={grid(3)} />
          <View paddingLeft={1} />
          <MBTextBody1 color={colors.foregroundActive}>{optionsData.title}</MBTextBody1>
        </View>
        {!isNil(firstSectionContent) && firstSectionContent}
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        {injectSectionsIn === 'top' && injectedSections}
        {renderLabelInput && (
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {labelForLabel}
            </MBTextOverlineBold>
            <View padding={1} />
            <TextInput
              autosize={isAuditComponent(component)}
              value={label}
              onChangeText={setLabelAndPlaceholder}
            />
          </View>
        )}
        {renderPlaceholderInput && (
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('workflowTemplate.placeholder')}
            </MBTextOverlineBold>
            <View padding={1} />
            <TextInput value={placeholder} onChangeText={setPlaceholder} />
          </View>
        )}
        {renderDivider && (
          <View paddingHorizontal={2} paddingTop={1}>
            <Divider />
          </View>
        )}
        {injectSectionsIn === 'bottom' && injectedSections}
        {!isNil(extraOptions) && (
          <View paddingHorizontal={2} paddingTop={1}>
            <Divider />
          </View>
        )}
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.options')}
          </MBTextOverlineBold>
        </View>
        {!isNil(extraOptions) && extraOptions}
        <ArchiveRow disabled={!!component.locked} onArchive={onArchive} />
        {isDeveloperToolsEnabled && <InternalOptions component={component} />}
      </RCMenu>
    </View>
  );
}

interface InternalOptionsProps {
  component: Component;
}

function InternalOptions({ component }: InternalOptionsProps) {
  const colors = useContext(ColorThemeContext);
  const dispatch = useWorkflowDispatch();
  const [hidden, setHidden] = useSetComponentField<boolean, Component>(component, 'isHidden');
  const required = isInputComponent(component) ? component.required : undefined;
  const setRequired = useCallback(
    (value: boolean) => {
      if (!isInputComponent(component)) {
        return;
      }
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          required: value,
        },
      });
    },
    [dispatch, component],
  );

  return (
    <>
      <View paddingHorizontal={2} paddingTop={1}>
        <Divider />
      </View>
      <View paddingHorizontal={3} paddingVertical={1}>
        <MBTextOverlineBold color={colors.error.foreground}>
          {i18n('workflowTemplate.internalOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={3} paddingVertical={1} row>
        <MBIconV2 name="eye-show" color={colors.foregroundActive} size={grid(3)} />
        <View fill centerVertically paddingLeft={1}>
          <MBTextBody1>{i18n('workflowTemplate.hidden')}</MBTextBody1>
        </View>
        <Switch value={hidden ?? false} onValueChange={() => setHidden(!hidden)} />
      </View>
      {isInputComponent(component) && (
        <View paddingHorizontal={3} paddingVertical={1} row>
          <MBIconV2 name="star" color={colors.foregroundActive} size={grid(3)} />
          <View fill centerVertically paddingLeft={1}>
            <MBTextBody1>{i18n('workflowTemplate.required')}</MBTextBody1>
          </View>
          <Switch value={required ?? false} onValueChange={() => setRequired(!required)} />
        </View>
      )}
    </>
  );
}
